// Field detection utilities for CSV import

// Synonym dictionary for common field variations
const FIELD_SYNONYMS = {
  firstName: [
    'firstname', 'first_name', 'fname', 'given_name', 'givenname',
    'first', 'forename', 'christian_name', 'prenom'
  ],
  lastName: [
    'lastname', 'last_name', 'lname', 'surname', 'family_name',
    'familyname', 'last', 'nom'
  ],
  name: [
    'fullname', 'full_name', 'name', 'display_name', 'displayname',
    'person_name', 'personname', 'contact_name', 'contactname'
  ],
  email: [
    'email', 'e_mail', 'email_address', 'emailaddress', 'mail',
    'email_id', 'emailid', 'electronic_mail', 'contact_email'
  ],
  phone: [
    'phone', 'phone_number', 'phonenumber', 'telephone', 'tel',
    'mobile', 'cell', 'cellphone', 'mobile_number', 'contact_number',
    'contact_phone', 'phone_no', 'phoneno', 'number'
  ],
  organization: [
    'organization', 'organisation', 'company', 'company_name',
    'companyname', 'employer', 'firm', 'corporation', 'business',
    'workplace', 'org', 'enterprise', 'institution'
  ],
  role: [
    'role', 'title', 'job_title', 'jobtitle', 'position', 'designation',
    'job', 'occupation', 'profession', 'job_role', 'jobrole',
    'job_position', 'jobposition', 'post'
  ],
  linkedinUrl: [
    'linkedin', 'linkedin_url', 'linkedinurl', 'linkedin_profile',
    'linkedinprofile', 'li_url', 'linkedin_link', 'linkedinlink',
    'li_profile', 'social_linkedin'
  ],
  bio: [
    'bio', 'biography', 'description', 'about', 'summary', 'profile',
    'introduction', 'background', 'bio_description', 'biodescription',
    'personal_bio', 'personalbio', 'professional_bio'
  ],
  skills: [
    'skills', 'skill', 'expertise', 'competencies', 'competency',
    'capabilities', 'skill_set', 'skillset', 'technical_skills',
    'professional_skills', 'specialties', 'specializations'
  ],
  address: [
    'address', 'location', 'street', 'street_address', 'streetaddress',
    'mailing_address', 'mailingaddress', 'postal_address', 'residence',
    'home_address', 'homeaddress', 'physical_address', 'city_state_zip'
  ],
  website: [
    'website', 'web', 'url', 'homepage', 'site', 'web_url', 'weburl',
    'website_url', 'websiteurl', 'personal_website', 'personalwebsite',
    'web_address', 'webaddress', 'www'
  ],
  // Faana Leadership Quality Scores (nested structure)
  'faana.leadership_quality_scores.courage_score': [
    'courage', 'courage_score', 'couragescore', 'bravery_score',
    'courage_rating', 'couragerating', 'risk_taking', 'risktaking'
  ],
  'faana.leadership_quality_scores.vulnerability_score': [
    'vulnerability', 'vulnerability_score', 'vulnerabilityscore', 'openness_score',
    'vulnerability_rating', 'vulnerabilityrating', 'authenticity_score'
  ],
  'faana.leadership_quality_scores.purpose_driven_score': [
    'purpose', 'purpose_driven', 'purposedriven', 'purpose_score', 'purposescore',
    'mission_alignment', 'missionalignment', 'values_alignment'
  ],
  'faana.leadership_quality_scores.learning_agility_score': [
    'learning_agility', 'learningagility', 'learning_agility_score', 'agility',
    'learning_speed', 'adaptability', 'cognitive_flexibility'
  ],
  'faana.leadership_quality_scores.determination_score': [
    'determination', 'determination_score', 'determinationscore', 'grit',
    'persistence', 'perseverance', 'tenacity'
  ],
  'faana.leadership_quality_scores.self_awareness_score': [
    'self_awareness', 'selfawareness', 'self_awareness_score', 'emotional_intelligence',
    'emotionalintelligence', 'self_reflection', 'mindfulness'
  ],
  'faana.leadership_quality_scores.adaptability_score': [
    'adaptability', 'adaptability_score', 'adaptabilityscore', 'flexibility',
    'change_management', 'resilience', 'adjustment'
  ],
  'faana.leadership_quality_scores.cross_cultural_score': [
    'cross_cultural', 'crosscultural', 'cultural_intelligence', 'cultural_competence',
    'diversity', 'inclusion', 'global_mindset'
  ],

  // Faana Framework Scores (nested structure)
  'faana.framework_scores.holistic_health_score': [
    'holistic_health', 'holistichealth', 'wellness', 'health_score',
    'wellbeing', 'life_balance', 'overall_health'
  ],
  'faana.framework_scores.financial_fitness_score': [
    'financial_fitness', 'financialfitness', 'financial_health', 'money_management',
    'financial_literacy', 'resource_management', 'financial_acumen'
  ],
  'faana.framework_scores.community_engagement_score': [
    'community_engagement', 'communityengagement', 'social_impact', 'networking',
    'community_involvement', 'civic_engagement', 'social_responsibility'
  ],
  'faana.framework_scores.leadership_strength_score': [
    'leadership_strength', 'leadershipstrength', 'overall_leadership', 'leadership_effectiveness',
    'leadership_capability', 'leadership_competency', 'leadership_mastery'
  ],

  // Faana Growth Metrics (nested structure)
  'faana.growth_metrics.growth_trajectory': [
    'growth_trajectory', 'growthtrajectory', 'development_phase', 'maturity_level',
    'leadership_stage', 'growth_stage', 'trajectory'
  ],
  'faana.growth_metrics.growth_velocity': [
    'growth_velocity', 'growthvelocity', 'development_speed', 'growth_rate',
    'improvement_rate', 'velocity', 'development_pace'
  ],
  'faana.growth_metrics.assessment_date': [
    'assessment_date', 'assessmentdate', 'evaluation_date', 'review_date',
    'test_date', 'survey_date', 'last_assessment'
  ],
  'faana.growth_metrics.coach_name': [
    'coach_name', 'coachname', 'facilitator', 'mentor', 'trainer',
    'coach', 'development_coach', 'leadership_coach'
  ],

  // Faana Risk Assessment (nested structure)
  'faana.risk_assessment.growth_risk_index': [
    'growth_risk', 'growthrisk', 'risk_index', 'risk_score',
    'development_risk', 'barrier_score', 'challenge_index'
  ],
  'faana.risk_assessment.risk_factors': [
    'risk_factors', 'riskfactors', 'barriers', 'challenges',
    'obstacles', 'growth_barriers', 'development_challenges'
  ],

  // Legacy scores (for backward compatibility)
  leadershipScore: [
    'leadership', 'leadership_score', 'leadershipscore', 'leader_score',
    'leadership_rating', 'leadershiprating', 'leadership_assessment',
    'lead_score', 'leadscore'
  ],
  innovationScore: [
    'innovation', 'innovation_score', 'innovationscore', 'innovative_score',
    'innovation_rating', 'innovationrating', 'creativity_score',
    'creative_score', 'innovation_assessment'
  ],
  collaborationScore: [
    'collaboration', 'collaboration_score', 'collaborationscore',
    'teamwork_score', 'teamworkscore', 'collaboration_rating',
    'team_score', 'teamscore', 'cooperative_score'
  ],
  executionScore: [
    'execution', 'execution_score', 'executionscore', 'delivery_score',
    'deliveryscore', 'performance_score', 'performancescore',
    'execution_rating', 'executionrating'
  ],
  notes: [
    'notes', 'note', 'comments', 'comment', 'remarks', 'remark',
    'observations', 'memo', 'annotation', 'additional_notes',
    'additionalnotes', 'extra_notes', 'extranotes'
  ],
  tags: [
    'tags', 'tag', 'labels', 'label', 'categories', 'category',
    'keywords', 'keyword', 'classification', 'type', 'types',
    'groups', 'group'
  ]
};

// Patterns for detecting field types by content
const CONTENT_PATTERNS = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
  linkedinUrl: /linkedin\.com\/(in|company)\//i,
  website: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/
};

// Fuzzy string matching using Levenshtein distance
function levenshteinDistance(str1, str2) {
  const matrix = [];

  // Convert to lowercase for case-insensitive comparison
  str1 = str1.toLowerCase();
  str2 = str2.toLowerCase();

  // If strings are equal, distance is 0
  if (str1 === str2) return 0;

  // If one string is empty, distance is length of other
  if (str1.length === 0) return str2.length;
  if (str2.length === 0) return str1.length;

  // Initialize matrix
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}

// Calculate similarity percentage
function calculateSimilarity(str1, str2) {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  return ((maxLength - distance) / maxLength) * 100;
}

// Detect field mapping for a single header
function detectSingleFieldMapping(header, sampleData = null) {
  const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');

  // Check exact matches first
  for (const [fieldName, synonyms] of Object.entries(FIELD_SYNONYMS)) {
    if (synonyms.includes(normalizedHeader)) {
      return { field: fieldName, confidence: 100 };
    }
  }

  // Check partial matches
  for (const [fieldName, synonyms] of Object.entries(FIELD_SYNONYMS)) {
    for (const synonym of synonyms) {
      // Check if header contains synonym
      if (normalizedHeader.includes(synonym) || synonym.includes(normalizedHeader)) {
        return { field: fieldName, confidence: 85 };
      }

      // Check fuzzy matching
      const similarity = calculateSimilarity(normalizedHeader, synonym);
      if (similarity > 75) {
        return { field: fieldName, confidence: Math.round(similarity) };
      }
    }
  }

  // If we have sample data, try to detect by content
  if (sampleData && sampleData.length > 0) {
    const samples = sampleData.slice(0, 5); // Check first 5 samples

    // Check email pattern
    if (samples.some(s => s && CONTENT_PATTERNS.email.test(s))) {
      return { field: 'email', confidence: 70 };
    }

    // Check phone pattern
    if (samples.some(s => s && CONTENT_PATTERNS.phone.test(s))) {
      return { field: 'phone', confidence: 70 };
    }

    // Check LinkedIn URL pattern
    if (samples.some(s => s && CONTENT_PATTERNS.linkedinUrl.test(s))) {
      return { field: 'linkedinUrl', confidence: 90 };
    }

    // Check website pattern
    if (samples.some(s => s && CONTENT_PATTERNS.website.test(s))) {
      return { field: 'website', confidence: 70 };
    }

    // Check for score fields (numeric 0-100)
    const allNumeric = samples.every(s => !isNaN(s) && s >= 0 && s <= 100);
    if (allNumeric) {
      if (normalizedHeader.includes('leader')) {
        return { field: 'leadershipScore', confidence: 75 };
      }
      if (normalizedHeader.includes('innovat')) {
        return { field: 'innovationScore', confidence: 75 };
      }
      if (normalizedHeader.includes('collaborat') || normalizedHeader.includes('team')) {
        return { field: 'collaborationScore', confidence: 75 };
      }
      if (normalizedHeader.includes('execut') || normalizedHeader.includes('deliver')) {
        return { field: 'executionScore', confidence: 75 };
      }
    }
  }

  return null;
}

// Main function to detect field mappings for all headers
export function detectFieldMappings(headers, sampleData = {}) {
  const mappings = {};
  const usedFields = new Set();

  // First pass: high confidence matches
  headers.forEach(header => {
    const detection = detectSingleFieldMapping(header, sampleData[header]);
    if (detection && detection.confidence >= 85 && !usedFields.has(detection.field)) {
      mappings[header] = detection.field;
      usedFields.add(detection.field);
    }
  });

  // Second pass: lower confidence matches
  headers.forEach(header => {
    if (!mappings[header]) {
      const detection = detectSingleFieldMapping(header, sampleData[header]);
      if (detection && detection.confidence >= 70 && !usedFields.has(detection.field)) {
        mappings[header] = detection.field;
        usedFields.add(detection.field);
      }
    }
  });

  return mappings;
}

// Get confidence color based on percentage
export function getConfidenceColor(confidence) {
  if (confidence >= 90) return '#4caf50'; // Green
  if (confidence >= 75) return '#ff9800'; // Orange
  if (confidence >= 60) return '#f44336'; // Red
  return '#9e9e9e'; // Grey
}

// Get field description
export function getFieldDescription(fieldId) {
  const descriptions = {
    firstName: 'The person\'s first or given name',
    lastName: 'The person\'s last name or surname',
    name: 'The person\'s full name (use if names are not split)',
    email: 'Professional email address',
    phone: 'Primary contact phone number',
    organization: 'Current company or organization',
    role: 'Job title or position within the organization',
    linkedinUrl: 'LinkedIn profile URL',
    bio: 'Professional biography or description',
    skills: 'Comma-separated list of professional skills',
    address: 'Physical or mailing address',
    website: 'Personal or professional website URL',

    // Faana Leadership Quality Scores
    'faana.leadership_quality_scores.courage_score': 'Courage leadership score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.vulnerability_score': 'Vulnerability leadership score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.purpose_driven_score': 'Purpose-driven leadership score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.learning_agility_score': 'Learning agility score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.determination_score': 'Determination score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.self_awareness_score': 'Self-awareness score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.adaptability_score': 'Adaptability score (1.0-3.0) - Faana methodology',
    'faana.leadership_quality_scores.cross_cultural_score': 'Cross-cultural intelligence score (1.0-3.0) - Faana methodology',

    // Faana Framework Scores
    'faana.framework_scores.holistic_health_score': 'Holistic health score (0-100) - Faana methodology',
    'faana.framework_scores.financial_fitness_score': 'Financial fitness score (0-100) - Faana methodology',
    'faana.framework_scores.community_engagement_score': 'Community engagement score (0-100) - Faana methodology',
    'faana.framework_scores.leadership_strength_score': 'Overall leadership strength score (0-100) - Faana methodology',

    // Faana Growth Metrics
    'faana.growth_metrics.growth_trajectory': 'Growth trajectory phase (Emerging, Developing, Advanced, Master)',
    'faana.growth_metrics.growth_velocity': 'Growth velocity rate (Slow, Moderate, Rapid)',
    'faana.growth_metrics.assessment_date': 'Date of last formal assessment (YYYY-MM-DD format)',
    'faana.growth_metrics.coach_name': 'Name of Faana-certified coach or facilitator',

    // Faana Risk Assessment
    'faana.risk_assessment.growth_risk_index': 'Growth risk index (0-10, higher = more risk) - Faana methodology',
    'faana.risk_assessment.risk_factors': 'Comma-separated list of identified risk factors',

    // Legacy scores (for backward compatibility)
    leadershipScore: 'Leadership assessment score (0-100) - Legacy format',
    innovationScore: 'Innovation capability score (0-100) - Legacy format',
    collaborationScore: 'Collaboration effectiveness score (0-100) - Legacy format',
    executionScore: 'Execution capability score (0-100) - Legacy format',
    notes: 'Additional notes or comments',
    tags: 'Comma-separated tags for categorization'
  };

  return descriptions[fieldId] || 'Custom field';
}

// Validate field data
export function validateFieldData(fieldId, value) {
  if (!value || value === '') {
    return { valid: true }; // Empty values are valid
  }

  switch (fieldId) {
    case 'email':
      return {
        valid: CONTENT_PATTERNS.email.test(value),
        error: 'Invalid email format'
      };

    case 'phone':
      return {
        valid: CONTENT_PATTERNS.phone.test(value) || value.length >= 7,
        error: 'Invalid phone number format'
      };

    case 'linkedinUrl':
      return {
        valid: CONTENT_PATTERNS.linkedinUrl.test(value) || value.includes('linkedin'),
        error: 'Invalid LinkedIn URL'
      };

    case 'website':
      return {
        valid: CONTENT_PATTERNS.website.test(value) || value.includes('http'),
        error: 'Invalid website URL'
      };

    // Faana Leadership Quality Scores (1-3 scale)
    case 'faana.leadership_quality_scores.courage_score':
    case 'faana.leadership_quality_scores.vulnerability_score':
    case 'faana.leadership_quality_scores.purpose_driven_score':
    case 'faana.leadership_quality_scores.learning_agility_score':
    case 'faana.leadership_quality_scores.determination_score':
    case 'faana.leadership_quality_scores.self_awareness_score':
    case 'faana.leadership_quality_scores.adaptability_score':
    case 'faana.leadership_quality_scores.cross_cultural_score':
      {
      const leadershipScore = parseFloat(value);
      return {
        valid: !isNaN(leadershipScore) && leadershipScore >= 1.0 && leadershipScore <= 3.0,
        error: 'Leadership score must be between 1.0 and 3.0'
      };

    // Faana Framework Scores (0-100 scale)
      }
    case 'faana.framework_scores.holistic_health_score':
    case 'faana.framework_scores.financial_fitness_score':
    case 'faana.framework_scores.community_engagement_score':
    case 'faana.framework_scores.leadership_strength_score':
      {
      const frameworkScore = parseFloat(value);
      return {
        valid: !isNaN(frameworkScore) && frameworkScore >= 0 && frameworkScore <= 100,
        error: 'Framework score must be between 0 and 100'
      };

    // Faana Risk Assessment
      }
    case 'faana.risk_assessment.growth_risk_index':
      {
      const riskScore = parseFloat(value);
      return {
        valid: !isNaN(riskScore) && riskScore >= 0 && riskScore <= 10,
        error: 'Growth risk index must be between 0 and 10'
      };

    // Faana Growth Metrics - categorical values
      }
    case 'faana.growth_metrics.growth_trajectory':
      {
      const trajectoryOptions = ['Emerging', 'Developing', 'Advanced', 'Master'];
      return {
        valid: trajectoryOptions.includes(value),
        error: 'Growth trajectory must be one of: Emerging, Developing, Advanced, Master'
      };

      }
    case 'faana.growth_metrics.growth_velocity':
      {
      const velocityOptions = ['Slow', 'Moderate', 'Rapid'];
      return {
        valid: velocityOptions.includes(value),
        error: 'Growth velocity must be one of: Slow, Moderate, Rapid'
      };

      }
    case 'faana.growth_metrics.assessment_date':
      return {
        valid: /^\d{4}-\d{2}-\d{2}$/.test(value),
        error: 'Assessment date must be in YYYY-MM-DD format'
      };

    // Legacy scores (for backward compatibility)
    case 'leadershipScore':
    case 'innovationScore':
    case 'collaborationScore':
    case 'executionScore':
      {
      const legacyScore = parseFloat(value);
      return {
        valid: !isNaN(legacyScore) && legacyScore >= 0 && legacyScore <= 100,
        error: 'Score must be between 0 and 100'
      };

      }
    default:
      return { valid: true };
  }
}

export default {
  detectFieldMappings,
  detectSingleFieldMapping,
  getConfidenceColor,
  getFieldDescription,
  validateFieldData,
  calculateSimilarity
};